import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import { Menu, Transition } from "@headlessui/react";
import {
  ChevronDownIcon,
  FunnelIcon,
  Squares2X2Icon,
  ListBulletIcon,
} from "@heroicons/react/20/solid";
import { Product } from "../../common/models/product";
import { classNames, sendAuthenticatedRequest } from "../../common/functions";
import SearchResultsGrid from "./SearchResultsGrid";
import { LoadingPage } from "./LoadingPage";
import SearchResultsList from "./SearchResultsList";
import { AppliedFilter, FilterPanel } from "./Filters";
import { Link, useLocation, useNavigate } from "react-router-dom";
import useFirebaseAuthentication from "../../common/hooks/useFirebaseAuthentication";
import {
  useDimensionsUnit,
  useExperimentFlags,
  useRfqQueries,
  useSearchHistory,
} from "../../buyerStateDefs";
import { useNotifications } from "../../common/stateDefs";
import { NewProductsNotificationForm } from "./NewProductsNotificationForm";
import { usePostHog } from "posthog-js/react";
import { SearchFeedbackWrapper } from "../SearchFeedback";
import { KonoGuaranteeBadge } from "../KonoGuaranteeBadge";
import { captureAnalyticsEvent } from "../../analytics";
import {
  compareRankableEntries,
  getMatchAssessment,
} from "./helpers";
import { priceValueForSort } from "./price-helpers";
import { collapseIdenticalResults } from "./dedupe-helpers";
import { useHelpAndRfq } from "../../buyerStateDefs";
import { notifyFifthProductScrolled } from "../ExitIntent/exitIntentTriggers";

const dimUnitOptions = [
  { name: "in", value: "in" },
  { name: "ft", value: "ft" },
  { name: "mm", value: "mm" },
  { name: "m", value: "m" },
];

// Number of "best deals" shown before the buyer opts into the full list.
const BEST_DEALS_COUNT = 5;

type SearchResultsViewProps = {
  products: Product[];
  query?: string;
  structuredQuery?: any;
  loading: boolean;
  loadingMore: boolean;
  totalResults: number;
  onLoadMore: () => void;
  /**
   * Set when the last "load more" page failed. While present, infinite scroll
   * stays off and the buyer gets an explicit retry button instead — a failed
   * page must never re-request itself just because the sentinel is still on
   * screen (that loop once fired ~1,200 requests from one browser and got a
   * real customer burst-blocked).
   */
  loadMoreError?: string;
  onRetryLoadMore?: () => void;
  /**
   * The first page failed (backend hiccup, not a browsing limit). An empty
   * list then means "we don't know", not "no matches": no dead-end copy and
   * no automatic hand-off to the help assistant.
   */
  searchFailed?: boolean;
  selectedFilters: AppliedFilter[];
};

export default function SearchResultsView({
  products,
  query,
  structuredQuery,
  loading,
  loadingMore,
  totalResults,
  onLoadMore,
  loadMoreError,
  onRetryLoadMore,
  searchFailed = false,
  selectedFilters,
}: SearchResultsViewProps) {
  const savedSearchConfig = JSON.parse(
    localStorage.getItem("savedSearchConfig") || "{}",
  );
  const dimensionUnit = useDimensionsUnit((state) => state.dimensionsUnit);
  const setDimensionUnit = useDimensionsUnit(
    (state) => state.setDimensionsUnit,
  );
  const [viewType, setViewType] = useState<"list" | "grid">(
    ["list", "grid"].includes(savedSearchConfig.viewType || "")
      ? savedSearchConfig.viewType
      : "list",
  );
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  // We need to keep track of the applied filters locally so that the filter
  // changes show up in the filter panel immediately, even if the search results
  // are still loading. Also the filter panel may show filters that are not
  // explicitly applied but come from the structured query instead.
  const [localAppliedFilters, setLocalAppliedFilters] = useState<
    AppliedFilter[]
  >([]);
  const rfqQueries = useRfqQueries((state) => state.rfqQueries);
  const setRfqQueries = useRfqQueries((state) => state.setRfqQueries);
  // Show only the best few deals by default; the buyer can reveal every match.
  const [showAllMatches, setShowAllMatches] = useState(false);
  const openRfq = useHelpAndRfq((s) => s.openRfq);
  const openHelp = useHelpAndRfq((s) => s.openHelp);

  // Rank ALL loaded results with the operator-configured Match & Ranking Rules
  // (same comparator the backend uses). Sorting client-side keeps the list
  // globally ordered even as more pages are appended, so a better match can
  // never appear below a worse one.
  const rankedProducts = useMemo(() => {
    // Must mirror the backend's price tiebreak (raw value, unpriced last).
    // Converting to $/lb here made per-unit-priced items unrankable and
    // shuffled the server's ordering.
    const comparablePrice = (p: Product) => priceValueForSort(p);
    return products
      .map((p, i) => ({
        p,
        entry: {
          assessment: getMatchAssessment(p, structuredQuery),
          comparablePrice: comparablePrice(p),
          inventoryLevel: p.quantity?.value ?? 0,
          originalIndex: i,
        },
      }))
      .sort((a, b) => compareRankableEntries(a.entry, b.entry))
      .map((x) => x.p);
  }, [products, structuredQuery]);

  // Collapse identical offers (same specs, different vendor -> cheapest wins)
  // and annotate near-identical rows with the spec fields that differ.
  const dedupedProducts = useMemo(
    () => collapseIdenticalResults(rankedProducts),
    [rankedProducts],
  );

  // Collapse back to the best deals whenever a new search runs.
  useEffect(() => {
    setShowAllMatches(false);
  }, [query]);

  // A search with nothing to show is a dead end, and plenty of those queries
  // (an alloy we can source but don't list, an unfamiliar spec name) do have a
  // useful answer. Hand the query to the help assistant automatically — once
  // per query, so closing the window doesn't immediately reopen it.
  const autoAskedQueryRef = useRef<string | null>(null);
  // Only fire after a search actually RAN for this query (we saw it loading).
  // On first render with a ?q= URL, products is empty simply because the
  // request has not started yet, and popping "we didn't find it" over 2000
  // perfectly good matches cost us exactly that embarrassment once.
  const searchRanForQueryRef = useRef<string | null>(null);
  useEffect(() => {
    if (loading) searchRanForQueryRef.current = (query || "").trim();
  }, [loading, query]);
  const noResultsQuestion = (q: string) =>
    `Do you have ${q} in your inventory? If not, can Kono source it?`;
  useEffect(() => {
    const q = (query || "").trim();
    if (loading || products.length > 0 || totalResults > 0 || !q) return;
    if (searchFailed) return;
    if (searchRanForQueryRef.current !== q) return;
    if (autoAskedQueryRef.current === q) return;
    // Hold for a beat before opening: overlapping searches for the same query
    // can render a momentary "done, zero results" frame while the replacement
    // request is still in flight. If anything changes (loading resumes,
    // results land), the cleanup cancels the open. Only an empty state that
    // SURVIVES is a real dead end.
    const timer = setTimeout(() => {
      autoAskedQueryRef.current = q;
      openHelp({
        notice: `We didn't find “${q}” in our online inventory — let me check what we can do.`,
        initialQuestion: noResultsQuestion(q),
      });
    }, 1500);
    return () => clearTimeout(timer);
  }, [loading, products.length, totalResults, query, openHelp, searchFailed]);

  // Both views use the ranked order: the full list must be globally sorted
  // (best match ALWAYS above worse matches), not the raw per-page server order.
  const resultsToDisplay = loading
    ? []
    : showAllMatches
      ? dedupedProducts
      : dedupedProducts.slice(0, BEST_DEALS_COUNT);
  const hasHiddenMatches = !loading && totalResults > BEST_DEALS_COUNT;

  // First-time leave-window: after an initial search, once the visitor has
  // scrolled through the first five listing rows.
  useEffect(() => {
    const q = (query || "").trim();
    if (loading || !q || resultsToDisplay.length === 0) return;
    let fired = false;
    const check = () => {
      if (fired) return;
      const rows = document.querySelectorAll("[data-search-result]");
      if (!rows.length) return;
      const fifth = rows[Math.min(4, rows.length - 1)] as HTMLElement;
      const rect = fifth.getBoundingClientRect();
      if (rect.bottom > 64 && rect.top < window.innerHeight - 40) {
        fired = true;
        notifyFifthProductScrolled();
      }
    };
    const timer = window.setTimeout(check, 400);
    window.addEventListener("scroll", check, { passive: true });
    return () => {
      window.clearTimeout(timer);
      window.removeEventListener("scroll", check);
    };
  }, [loading, query, resultsToDisplay.length]);
  const loadMoreTriggerRef = useRef<HTMLDivElement | null>(null);
  const { authUser } = useFirebaseAuthentication();
  const dummySearchExperimentFlag = useExperimentFlags(
    (state) => state.dummySearchExperimentVariant,
  );
  const posthog = usePostHog();

  const navigate = useNavigate();

  const updateSearchConfig = (newConfig: {
    viewType?: "list" | "grid";
    dimUnit?: string;
  }) => {
    if (!newConfig) return;
    if (newConfig.dimUnit) {
      setDimensionUnit(newConfig.dimUnit);
    }
    if (newConfig.viewType) {
      setViewType(newConfig.viewType);
    }
    const currentConfig = JSON.parse(
      localStorage.getItem("savedSearchConfig") || "{}",
    );
    const updatedConfig = { ...currentConfig, ...newConfig };
    localStorage.setItem("savedSearchConfig", JSON.stringify(updatedConfig));
  };

  const toggleViewType = () => {
    const newViewType = viewType === "list" ? "grid" : "list";
    updateSearchConfig({ viewType: newViewType });
  };

  useEffect(() => {
    if (savedSearchConfig.dimUnit) {
      setDimensionUnit(savedSearchConfig.dimUnit);
    }
  }, []);

  useEffect(() => {
    const newAppliedFilters: AppliedFilter[] = [...selectedFilters];
    // Only apply the shape filter from the structured query if no other shape filter is applied
    if (
      structuredQuery?.shape &&
      selectedFilters.filter((f) => f.filter === "shape").length === 0
    ) {
      newAppliedFilters.push({
        filter: "shape",
        value: structuredQuery?.shape,
      });
    }
    setLocalAppliedFilters(newAppliedFilters);
  }, [structuredQuery, selectedFilters]);

  const hasMoreResults =
    showAllMatches &&
    !loading &&
    !loadingMore &&
    !loadMoreError &&
    totalResults > products.length;

  useEffect(() => {
    const triggerElement = loadMoreTriggerRef.current;
    if (!triggerElement || !hasMoreResults) {
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          onLoadMore();
        }
      },
      {
        rootMargin: "200px 0px",
      },
    );

    observer.observe(triggerElement);

    return () => {
      observer.disconnect();
    };
  }, [hasMoreResults, onLoadMore, resultsToDisplay.length, viewType]);

  const formatQueryStructureDebugComponent = (component: any) => {
    if (
      typeof component === "object" &&
      !Array.isArray(component) &&
      Object.values(component).every(
        (v) => typeof v === "object" && !!(v as any).value && !!(v as any).unit,
      )
    ) {
      return (
        <>
          {Object.keys(component).map((k) => (
            <span>
              {k} {component[k].value} {component[k].unit}{" "}
            </span>
          ))}
        </>
      );
    }
    return JSON.stringify(component).replace(/^"+|"+$/g, "");
  };

  const querySearchComponentEmpty = (component: any) => {
    if (typeof component === "object" && !Array.isArray(component)) {
      return Object.keys(component).length === 0;
    }
    return component === null || component === "";
  };

  const applyNewFilters = (newFilters: AppliedFilter[]) => {
    setLocalAppliedFilters(newFilters);
    navigate(
      `/?q=${query}&` +
        newFilters.map((f) => `${f.filter}=${f.value}`).join("&"),
    );
  };

  const trackSearchResultClick = (
    searchResultRank: number,
    product: Product,
    query: string,
  ) => {
    captureAnalyticsEvent(posthog, "search_result_clicked", {
      result_position: searchResultRank,
      search_query: query,
      product_id: product.id,
      $feature_flag: "search-engine-experiment", // Add this
      search_engine_variant: dummySearchExperimentFlag,
    });
  };

  const removeRfqQuery = (q: string) => {
    const updatedRfqQueries = rfqQueries.filter((item) => item !== q);
    setRfqQueries(updatedRfqQueries);
  };

  return (
    <>
      {!!rfqQueries && rfqQueries.length > 1 && (
        <div className="text-sm mt-5 md:relative md:top-[4em]">
          <span className="font-medium">RFQ items</span>{" "}
          <a
            className="text-gray-500 ml-2 text-xs cursor-pointer"
            onClick={(e) => {
              e.preventDefault();
              setRfqQueries([]);
            }}
          >
            Dismiss
          </a>
          <div className="flex flex-wrap mt-2">
            {rfqQueries.map((q) => (
              <div className="relative" key={q}>
                <Link
                  to={`/?q=${q}`}
                  className={classNames(
                    "p-1 m-1 rounded-full inline-block border border-[#00062e] hover:bg-gray-200",
                    q === query && "bg-gray-100",
                  )}
                >
                  {q}
                </Link>
                <div className="absolute top-0 right-0 rounded-full bg-gray-900 cursor-pointer w-4 h-4">
                  <div
                    className="text-white -mt-[0.2em] ml-[0.2em]"
                    onClick={() => removeRfqQuery(q)}
                  >
                    ×
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="bg-white">
        <div>
          <main className="mx-auto max-w-7xl px-4">
            <div className="pt-[5.7em]"></div>
            <div className="lg:sticky lg:top-[5.7em] lg:pt-5 lg:z-30 bg-white">
              <div className="flex items-baseline justify-between border-b border-gray-200 pb-6">
                <div>
                  <div id="search-overall-annotations-container"></div>
                </div>
                <div className="flex items-center">
                  <div className="text-sm font-medium text-gray-700 hover:text-gray-900">
                    {loading
                      ? ""
                      : showAllMatches
                        ? `${resultsToDisplay.length}${
                            totalResults > resultsToDisplay.length
                              ? ` of ${totalResults}`
                              : ""
                          } results`
                        : hasHiddenMatches
                          ? `Top ${resultsToDisplay.length} of ${totalResults} matches`
                          : `${resultsToDisplay.length} results`}
                  </div>
                  <KonoGuaranteeBadge small compact className="ml-4" />
                  <Menu
                    as="div"
                    className="relative inline-block text-left ml-5 sm:ml-7"
                  >
                    <div>
                      <Menu.Button className="group inline-flex justify-center text-sm font-medium text-gray-700 hover:text-gray-900">
                        Unit: {dimensionUnit}
                        <ChevronDownIcon
                          className="-mr-1 ml-1 h-5 w-5 flex-shrink-0 text-gray-400 group-hover:text-gray-500"
                          aria-hidden="true"
                        />
                      </Menu.Button>
                    </div>

                    <Transition
                      as={Fragment}
                      enter="transition ease-out duration-100"
                      enterFrom="transform opacity-0 scale-95"
                      enterTo="transform opacity-100 scale-100"
                      leave="transition ease-in duration-75"
                      leaveFrom="transform opacity-100 scale-100"
                      leaveTo="transform opacity-0 scale-95"
                    >
                      <Menu.Items className="absolute right-0 z-10 mt-2 w-40 origin-top-right rounded-md bg-white shadow-2xl ring-1 ring-black ring-opacity-5 focus:outline-none">
                        <div className="py-1">
                          {dimUnitOptions.map((option) => (
                            <Menu.Item key={option.name}>
                              {({ active }) => (
                                <a
                                  href="#"
                                  onClick={(e) => {
                                    e.preventDefault();
                                    updateSearchConfig({
                                      dimUnit: option.value,
                                    });
                                  }}
                                  className={classNames(
                                    option.value == dimensionUnit
                                      ? "font-medium text-gray-900"
                                      : "text-gray-500",
                                    active ? "bg-gray-100" : "",
                                    "block px-4 py-2 text-sm",
                                  )}
                                >
                                  {option.name}
                                </a>
                              )}
                            </Menu.Item>
                          ))}
                        </div>
                      </Menu.Items>
                    </Transition>
                  </Menu>

                  <button
                    type="button"
                    className="-m-2 ml-5 p-2 text-gray-400 hover:text-gray-500 sm:ml-7"
                    onClick={toggleViewType}
                  >
                    {viewType === "list" ? (
                      <>
                        <span className="sr-only">View grid</span>
                        <Squares2X2Icon
                          className="h-5 w-5"
                          aria-hidden="true"
                        />
                      </>
                    ) : (
                      <>
                        <span className="sr-only">View list</span>
                        <ListBulletIcon
                          className="h-5 w-5"
                          aria-hidden="true"
                        />
                      </>
                    )}
                  </button>
                  <button
                    type="button"
                    className="-m-2 ml-4 p-2 text-gray-400 hover:text-gray-500 sm:ml-6 lg:hidden"
                    onClick={() => setMobileFiltersOpen(true)}
                  >
                    <span className="sr-only">Filters</span>
                    <FunnelIcon className="h-5 w-5" aria-hidden="true" />
                  </button>
                </div>
              </div>
            </div>

            <section aria-labelledby="products-heading" className="pb-24 pt-6">
              <h2 id="products-heading" className="sr-only">
                Products
              </h2>

              <div className="flex">
                <div className="w-[25%] hidden lg:block">
                  <div className="lg:sticky lg:top-[10.25rem] lg:max-h-[calc(100vh-11rem)] lg:overflow-y-auto lg:overscroll-contain lg:pr-2">
                    <FilterPanel
                      appliedFilters={localAppliedFilters}
                      onChanged={applyNewFilters}
                      mobileFiltersOpen={mobileFiltersOpen}
                      setMobileFiltersOpen={setMobileFiltersOpen}
                      structuredQuery={structuredQuery}
                      query={query}
                    />
                    {!!authUser && <SaveSearch />}
                  </div>
                </div>
                <div className="w-full pl-5">
                  {loading && <LoadingPage viewType={viewType} />}
                  {!loading && !!products.length && (
                    <>
                      {viewType === "grid" ? (
                        <SearchResultsGrid
                          products={resultsToDisplay}
                          structuredQuery={structuredQuery}
                          trackSearchResultClick={trackSearchResultClick}
                        />
                      ) : (
                        <SearchResultsList
                          products={resultsToDisplay}
                          structuredQuery={structuredQuery}
                          trackSearchResultClick={trackSearchResultClick}
                        />
                      )}
                      <div className="mt-6 flex flex-wrap justify-center items-center gap-3">
                        {!showAllMatches && hasHiddenMatches && (
                          <button
                            type="button"
                            onClick={() => setShowAllMatches(true)}
                            className="rounded-full border-2 border-[#00062e] px-6 py-2 text-sm font-bold text-[#00062e] hover:bg-slate-100"
                          >
                            Show all matches ({totalResults})
                          </button>
                        )}
                        <button
                          type="button"
                          onClick={() => openRfq()}
                          className="rounded-full border-2 border-[#00062e] px-6 py-2 text-sm font-bold text-[#00062e] hover:bg-slate-100"
                        >
                          Upload RFQ
                        </button>
                        <button
                          type="button"
                          onClick={() => openHelp()}
                          className="rounded-full border-2 border-[#00062e] px-6 py-2 text-sm font-bold text-[#00062e] hover:bg-slate-100"
                        >
                          Need Help
                        </button>
                      </div>
                      {showAllMatches && (
                        <div
                          ref={loadMoreTriggerRef}
                          className="flex min-h-[4rem] items-center justify-center"
                        >
                          {loadingMore && (
                            <div
                              className="h-8 w-8 animate-spin rounded-full border-4 border-gray-200 border-t-gray-700"
                              aria-label="Loading more results"
                            ></div>
                          )}
                          {!loadingMore && loadMoreError && (
                            <div className="flex flex-col items-center gap-2 text-center text-sm text-gray-700">
                              <span>{loadMoreError}</span>
                              {onRetryLoadMore && (
                                <button
                                  type="button"
                                  onClick={onRetryLoadMore}
                                  className="rounded-full border-2 border-[#00062e] px-5 py-1.5 text-sm font-bold text-[#00062e] hover:bg-slate-100"
                                >
                                  Load more results
                                </button>
                              )}
                            </div>
                          )}
                        </div>
                      )}
                    </>
                  )}
                  {!loading && !searchFailed && !products.length && (
                    <div className="mx-auto max-w-xl text-center">
                      <p className="text-gray-700">
                        {query?.trim()
                          ? `No online matches for “${query.trim()}”.`
                          : "No results found"}
                      </p>
                      {query?.trim() && (
                        <>
                          <p className="mt-2 text-sm text-gray-500">
                            Not everything we can supply is listed online. Our
                            assistant is checking whether we can source it — or
                            send us the full request and a specialist will reply.
                          </p>
                          <div className="mt-4 flex flex-wrap items-center justify-center gap-3">
                            <button
                              type="button"
                              onClick={() =>
                                openHelp({
                                  initialQuestion: noResultsQuestion(
                                    query.trim(),
                                  ),
                                })
                              }
                              className="rounded-full bg-[#00062e] px-5 py-2 text-sm font-bold text-white hover:bg-[#009499]"
                            >
                              Ask about “{query.trim()}”
                            </button>
                            <button
                              type="button"
                              onClick={openRfq}
                              className="rounded-full border border-[#00062e] px-5 py-2 text-sm font-bold text-[#00062e] hover:bg-gray-100"
                            >
                              Upload RFQ
                            </button>
                          </div>
                        </>
                      )}
                    </div>
                  )}
                  {!loading && !searchFailed && !authUser && !products.length && (
                    <NewProductsNotificationForm
                      searchResults={resultsToDisplay}
                      query={query}
                    />
                  )}
                </div>
              </div>
            </section>
          </main>
          <SearchFeedbackWrapper />
        </div>
      </div>
    </>
  );
}

const SaveSearch = () => {
  const location = useLocation();
  const totalUserSearchHistory = useSearchHistory(
    (state) => state.totalUserSearchHistory,
  );
  const addNotification = useNotifications((state) => state.addNotification);

  const urlQueryString = location.search;

  const isSaved = totalUserSearchHistory.some(
    (item) => item.urlQueryString === urlQueryString,
  );

  const handleSubmit = (remove: boolean) => {
    sendAuthenticatedRequest(
      `${process.env.REACT_APP_SEARCH_JS_API_BASE_URL}/save`,
      {
        method: "POST",
        jsonBody: {
          urlQueryString,
          remove,
        },
      },
    )
      .then((response) => {
        const title = remove ? "Search removed" : "Search saved";
        const content = remove ? (
          "The current search query and filters have been removed from your Search History."
        ) : (
          <>
            <p>
              The current search query and filters can now be accessesed from
              your Search History.
            </p>
            <p className="pt-1">
              Note that the results are always live, so may be different the
              next time you use the saved search!
            </p>
          </>
        );

        addNotification({
          id: String(Math.random()),
          title,
          content,
          type: "success",
        });
      })
      .catch((error) => {
        console.error("Error saving search:", error);
        addNotification({
          id: String(Math.random()),
          title: "Error saving search",
          content:
            "The search parameters could not be saved. Please try again.",
          type: "error",
        });
      });
  };

  return (
    <div className="pt-5">
      <button
        type="button"
        className="w-full rounded-full border border-gray-600 text-gray-600 text-sm py-1 text-center hover:bg-gray-100 hover:text-gray-900 focus:outline-none focus:ring-2 focus:ring-gray-600 focus:ring-offset-2"
        onClick={() => handleSubmit(isSaved)}
      >
        {isSaved ? "Remove Saved Search" : "Save Search"}
      </button>
    </div>
  );
};
